// src/pages/Invoices/Report/PurchaseItemsSummary.jsx
// ملخص مشتريات الأصناف — سطر لكل صنف: الكميات المشتراة والمرتجعة، متوسط التكلفة، آخر سعر، والمبالغ

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Autocomplete, TextField, Typography } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GridOnIcon from "@mui/icons-material/GridOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

import axios from "../../../axios";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from "../../../components/Reports/ReportKit";

const money = (color, bold = 700) => (p) => (
  <Typography sx={{ direction: "ltr", fontWeight: bold, fontSize: 13.5, color: Number(p.value) < 0 ? "#be123c" : color }}>
    {fmtMoney(p.value)}
  </Typography>
);

const qty = (color) => (p) => (
  <Typography sx={{ direction: "ltr", fontWeight: 700, fontSize: 13.5, color: Number(p.value) === 0 ? "#cbd5e1" : color }}>
    {fmtNumber(p.value)}
  </Typography>
);

export default function PurchaseItemsSummary() {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const range = useReportDateRange("today");
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);

  const [branchId, setBranchId] = useState("");
  const [stockId, setStockId] = useState("");
  const [product, setProduct] = useState(null);
  const [search, setSearch] = useState("");

  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data || [])).catch(() => {});
    axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data || [])).catch(() => {});
  }, [companyId]);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get("/Invoice/PurchaseItemsSummary", {
        params: {
          companyId,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
          branchId: branchId ? Number(branchId) : null,
          stockId: stockId ? Number(stockId) : null,
          productId: product?.productId || null,
        },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  }, [companyId, range.dateFrom, range.dateTo, branchId, stockId, product]);

  useEffect(() => { if (ready) fetchReport(); }, [ready, fetchReport]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.productNameAr ?? "").toLowerCase().includes(q) ||
      (r.productNameEn ?? "").toLowerCase().includes(q) ||
      String(r.productId).includes(q));
  }, [rows, search]);

  const totals = useMemo(() => visibleRows.reduce((a, r) => ({
    purchasedQty: a.purchasedQty + (Number(r.purchasedQty) || 0),
    returnedQty: a.returnedQty + (Number(r.returnedQty) || 0),
    gross: a.gross + (Number(r.gross) || 0),
    discount: a.discount + (Number(r.discount) || 0),
    vat: a.vat + (Number(r.vat) || 0),
    net: a.net + (Number(r.net) || 0),
  }), { purchasedQty: 0, returnedQty: 0, gross: 0, discount: 0, vat: 0, net: 0 }), [visibleRows]);

  const columns = useMemo(() => [
    { field: "productId", headerName: "الرقم", width: 75, headerAlign: "center", align: "center" },
    {
      field: "productNameAr", headerName: "الصنف", flex: 1.5, minWidth: 190, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{p.value || p.row.productNameEn || "—"}</Typography>,
    },
    { field: "invoicesCount", headerName: "الفواتير", type: "number", width: 85, headerAlign: "center", align: "center", renderCell: qty("#334155") },
    { field: "purchasedQty", headerName: "المشترى", type: "number", width: 95, headerAlign: "center", align: "center", renderCell: qty("#047857") },
    { field: "returnedQty", headerName: "المرتجع", type: "number", width: 90, headerAlign: "center", align: "center", renderCell: qty("#be123c") },
    {
      field: "netQty", headerName: "صافي الكمية", type: "number", width: 105, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 13.5 }}>{fmtNumber(p.value)}</Typography>,
    },
    { field: "avgUnitCost", headerName: "متوسط التكلفة", type: "number", width: 115, headerAlign: "center", align: "center", renderCell: money("#4338ca") },
    { field: "lastUnitPrice", headerName: "آخر سعر", type: "number", width: 100, headerAlign: "center", align: "center", renderCell: money("#334155", 600) },
    {
      field: "lastPurchaseDate", headerName: "آخر شراء", width: 110, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontSize: 13, color: "#475569" }}>{p.value ? dayjs(p.value).format("YYYY-MM-DD") : "—"}</Typography>,
    },
    { field: "gross", headerName: "الإجمالي", type: "number", width: 115, headerAlign: "center", align: "center", renderCell: money("#0f172a") },
    { field: "discount", headerName: "الخصم", type: "number", width: 100, headerAlign: "center", align: "center", renderCell: money("#e11d48") },
    { field: "vat", headerName: "الضريبة", type: "number", width: 100, headerAlign: "center", align: "center", renderCell: money("#d97706") },
    { field: "net", headerName: "الصافي", type: "number", width: 120, headerAlign: "center", align: "center", renderCell: money("#059669", 800) },
  ], []);

  const exportExcel = () => {
    if (!visibleRows.length) return toast.info("لا توجد بيانات للتصدير");
    const data = visibleRows.map(r => ({
      "الرقم": r.productId,
      "الصنف": r.productNameAr || r.productNameEn,
      "عدد الفواتير": r.invoicesCount,
      "الكمية المشتراة": r.purchasedQty,
      "الكمية المرتجعة": r.returnedQty,
      "صافي الكمية": r.netQty,
      "متوسط التكلفة": r.avgUnitCost,
      "آخر سعر": r.lastUnitPrice,
      "آخر شراء": r.lastPurchaseDate ? dayjs(r.lastPurchaseDate).format("YYYY-MM-DD") : "",
      "الإجمالي": r.gross,
      "الخصم": r.discount,
      "الضريبة": r.vat,
      "الصافي": r.net,
    }));
    data.push({
      "الرقم": "", "الصنف": "الإجمالي",
      "الكمية المشتراة": totals.purchasedQty, "الكمية المرتجعة": totals.returnedQty,
      "صافي الكمية": totals.purchasedQty - totals.returnedQty,
      "الإجمالي": totals.gross, "الخصم": totals.discount, "الضريبة": totals.vat, "الصافي": totals.net,
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!views"] = [{ RTL: true }];
    ws["!cols"] = Object.keys(data[0]).map(k => ({ wch: k === "الصنف" ? 32 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ملخص مشتريات الأصناف");
    XLSX.writeFile(wb, `PurchaseItemsSummary_${range.dateFrom}_${range.dateTo}.xlsx`);
  };

  const resetFilters = () => {
    range.reset();
    setBranchId(defaultBranch);
    setStockId("");
    setProduct(null);
    setSearch("");
  };

  const bName = branchName(branchId);

  return (
    <ReportPage
      title="ملخص مشتريات الأصناف"
      subtitle="الكميات والتكلفة لكل صنف خلال الفترة — بعد طرح مرتجعات المشتريات"
      icon={<Inventory2OutlinedIcon />}
      period={range}
      badges={bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []}
      actions={[
        { label: "تحديث", icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: "Excel", icon: <GridOnIcon />, onClick: exportExcel, primary: true },
      ]}
      stats={[
        { label: "عدد الأصناف", value: fmtNumber(visibleRows.length), icon: <CategoryOutlinedIcon />, color: "primary" },
        { label: "الكميات المشتراة", value: fmtNumber(totals.purchasedQty), icon: <ShoppingCartOutlinedIcon />, color: "slate" },
        { label: "الكميات المرتجعة", value: fmtNumber(totals.returnedQty), icon: <AssignmentReturnOutlinedIcon />, color: "error" },
        { label: "الإجمالي", value: fmtMoney(totals.gross), icon: <AttachMoneyIcon />, color: "info" },
        { label: "الضريبة", value: fmtMoney(totals.vat), icon: <RequestQuoteOutlinedIcon />, color: "warning" },
        { label: "صافي المشتريات", value: fmtMoney(totals.net), icon: <AccountBalanceWalletIcon />, color: "success", highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="اسم الصنف أو رقمه..."
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={(branchId ? 1 : 0) + (stockId ? 1 : 0) + (product ? 1 : 0) + (search ? 1 : 0)}
        >
          <ReportSelect label="الفرع" value={branchId} onChange={setBranchId} options={branchOptions} />
          <ReportSelect
            label="المخزن" value={stockId} onChange={setStockId}
            options={stocks.map(s => ({ value: String(s.id), label: s.nameAr || s.name }))}
          />
          <Autocomplete
            size="small"
            options={products}
            value={product}
            onChange={(_, v) => setProduct(v)}
            getOptionLabel={(p) => p?.arabicName || p?.nameAr || ""}
            isOptionEqualToValue={(a, b) => a.productId === b.productId}
            noOptionsText="لا توجد نتائج"
            renderInput={(params) => <TextField {...params} label="الصنف" placeholder="الكل" sx={fieldSx} />}
          />
        </ReportFilters>
      }
    >
      <ReportTableCard title="الأصناف المشتراة" count={fmtNumber(visibleRows.length)}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          slots={{ toolbar: GridToolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          localeText={{
            noRowsLabel: "لا توجد مشتريات في هذه الفترة",
            toolbarColumns: "الأعمدة",
            toolbarFilters: "الفلاتر",
            toolbarDensity: "الكثافة",
            toolbarExport: "تصدير",
          }}
          sx={reportGridSx}
        />
      </ReportTableCard>
    </ReportPage>
  );
}
